import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import i18n from 'i18n';

import ErrorBoundary from 'layout/components/ErrorBoundary';
import PageShell from 'layout/components/PageShell';
import * as PageLayout from 'layout/styles/PageLayout.style';

import { useLogoutAllMutation } from 'auth/hooks/loginApi';
import {
  useUser,
  useUpdateIdentityMutation,
  useUpdateLanguageMutation,
} from 'shared/hooks/api/user';
import { usePosthogCapture } from 'settings/posthogCapture/_index';
import {
  useNotificationSettings,
  useUpdateNotificationSettings,
} from 'shared/hooks/api/notificationSettings';

import Account from 'settings/components/Account';
import { SupportedLanguage } from 'i18n/languages';
import InputMethod from 'settings/components/InputMethod';
import usePushNotifications from 'pwa/hooks/usePushNotifications';
import NotificationCards, {
  NOTIFICATION_TYPE_KEYS,
  NotificationTypeSettings,
} from 'settings/components/NotificationCards';
import NotificationsLoadState from 'settings/components/NotificationsLoadState';
import { ShiftScheduleSettings } from 'settings/components/ShiftSchedule';
import * as Styled from 'settings/styles/SettingsPage.style';
import findChangedNotificationType from 'settings/utils/findChangedNotificationType';
import {
  InputMethodValue,
  readStoredInputMethod,
  writeStoredInputMethod,
} from 'settings/utils/inputMethodStorage';
import { normalizeTimeToHHMM } from 'settings/utils/timeFormat';
import {
  daysToIntegers,
  integersToDays,
} from 'settings/utils/weekdayConversion';

const DEFAULT_TYPE_SETTINGS: NotificationTypeSettings = {
  newEvents: { enabled: true, channel: 'push' },
  shiftReports: { enabled: true, channel: 'email' },
  shiftReminders: { enabled: true, channel: 'email' },
};

const API_FIELDS = {
  newEvents: 'new_event_notifications',
  shiftReports: 'shift_report_notifications',
  shiftReminders: 'shift_reminder_notifications',
} as const;

const CAPTURE_TYPES = {
  newEvents: 'new events',
  shiftReports: 'shift reports',
  shiftReminders: 'shift reminders',
} as const;

const DEFAULT_SHIFT_SCHEDULE: ShiftScheduleSettings = {
  autoSchedule: true,
  time: '08:00',
  days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
};

export default function SettingsPage(): JSX.Element {
  const { t } = useTranslation('pageSettings');
  const { data: user } = useUser();
  const posthog = usePostHog();
  const settings = usePosthogCapture();
  const canAccessShiftNotes = posthog.isFeatureEnabled('shift_notes');
  const visibleTypes = NOTIFICATION_TYPE_KEYS.filter(
    (key) =>
      (key !== 'shiftReminders' || canAccessShiftNotes) &&
      (key !== 'shiftReports' || user?.is_team_lead)
  );

  const [nickname, setNickname] = useState<string>(user?.nickname || '');
  const [language, setLanguage] = useState<SupportedLanguage>(
    (user?.language as SupportedLanguage) || 'en'
  );
  const [inputMethod, setInputMethod] = useState<InputMethodValue>(
    readStoredInputMethod
  );

  const changeInputMethod = (next: InputMethodValue) => {
    setInputMethod(next);
    writeStoredInputMethod(next);
  };

  const { mutate: updateIdentity } = useUpdateIdentityMutation();
  const { mutate: updateLanguage } = useUpdateLanguageMutation();
  const { mutate: logoutAllDevices } = useLogoutAllMutation();

  const {
    data: notificationSettingsData,
    isError: hasNotificationSettingsFailed,
  } = useNotificationSettings();
  const pushNotifications = usePushNotifications();
  const { mutate: updateNotificationSettings } =
    useUpdateNotificationSettings();

  useEffect(() => {
    if (user?.nickname) setNickname(user.nickname);
    if (user?.language) setLanguage(user.language as SupportedLanguage);
  }, [user?.nickname, user?.language]);

  const typeSettings = useMemo<NotificationTypeSettings>(
    () => ({
      newEvents:
        notificationSettingsData?.new_event_notifications ??
        DEFAULT_TYPE_SETTINGS.newEvents,
      shiftReports:
        notificationSettingsData?.shift_report_notifications ??
        DEFAULT_TYPE_SETTINGS.shiftReports,
      shiftReminders:
        notificationSettingsData?.shift_reminder_notifications ??
        DEFAULT_TYPE_SETTINGS.shiftReminders,
    }),
    [notificationSettingsData]
  );

  const shiftScheduleValue = useMemo<ShiftScheduleSettings>(() => {
    if (!notificationSettingsData) return DEFAULT_SHIFT_SCHEDULE;

    return {
      autoSchedule: notificationSettingsData.autofill_notification_schedule,
      time:
        normalizeTimeToHHMM(notificationSettingsData.shift_notification_time) ??
        DEFAULT_SHIFT_SCHEDULE.time,
      days: integersToDays(
        notificationSettingsData.shift_notification_weekdays
      ),
    };
  }, [notificationSettingsData]);

  const saveNickname = (nextNickname: string) => {
    setNickname(nextNickname);
    settings.usernameEdited();
    updateIdentity({ nickname: nextNickname, pronouns: ['-'] });
  };

  const changeLanguage = (next: SupportedLanguage) => {
    setLanguage(next);
    i18n.changeLanguage(next);
    document.documentElement.lang = next;
    updateLanguage({ language: next });
  };

  const handleTypeSettingsChange = (next: NotificationTypeSettings) => {
    const key = findChangedNotificationType(typeSettings, next);
    if (!key) return;

    const previous = typeSettings[key];
    const changed = next[key];
    const type = CAPTURE_TYPES[key];

    updateNotificationSettings({ [API_FIELDS[key]]: changed });

    if (changed.enabled !== previous.enabled) {
      if (changed.enabled) settings.notificationsEnabled({ type });
      else settings.notificationsDisabled({ type });
    }
    if (changed.channel !== previous.channel) {
      settings.notificationsChannelChanged({ type, channel: changed.channel });
    }
  };

  const handleLogoutAllDevices = () => {
    logoutAllDevices();
    settings.loggedOutAllDevices();
    posthog?.reset();
  };

  const handleShiftScheduleChange = (next: ShiftScheduleSettings) => {
    updateNotificationSettings({
      autofill_notification_schedule: next.autoSchedule,
      shift_notification_time: next.time,
      shift_notification_weekdays: daysToIntegers(next.days),
    });
  };

  return (
    <ErrorBoundary>
      <PageLayout.Page>
        <PageShell title={t('title')}>
          <Styled.Page>
            <Account
              email={user?.email}
              nickname={nickname}
              onSaveNickname={saveNickname}
              language={language}
              officialLanguage={user?.official_language}
              onChangeLanguage={changeLanguage}
              onSignOutAllDevices={handleLogoutAllDevices}
            />

            <InputMethod value={inputMethod} onChange={changeInputMethod} />

            {notificationSettingsData ? (
              <NotificationCards
                value={typeSettings}
                onChange={handleTypeSettingsChange}
                types={visibleTypes}
                schedule={shiftScheduleValue}
                onScheduleChange={handleShiftScheduleChange}
                device={{
                  status: pushNotifications.status,
                  isBusy: pushNotifications.isBusy,
                  hasFailed: pushNotifications.hasFailed,
                  onEnable: pushNotifications.enable,
                }}
              />
            ) : (
              <NotificationsLoadState
                hasFailed={hasNotificationSettingsFailed}
              />
            )}

            <Styled.BottomSpacer />
          </Styled.Page>
        </PageShell>
      </PageLayout.Page>
    </ErrorBoundary>
  );
}
